import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@/lib/icons";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Search, Menu, LogOut, Crown, Zap } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: "LayoutDashboard" },
  { to: "/mentor", label: "ECO Mentor", icon: "Sparkles" },
  { to: "/decision-lab", label: "Decision Lab", icon: "Scale", pro: true },
  { to: "/knowledge", label: "Knowledge Universe", icon: "Library" },
  { to: "/university", label: "Founder University", icon: "GraduationCap" },
  { to: "/roadmap", label: "Founder Roadmap", icon: "Map", pro: true },
  { to: "/frameworks", label: "Framework Library", icon: "Grid2x2" },
  { to: "/dictionary", label: "Business Dictionary", icon: "BookA" },
  { to: "/case-studies", label: "Case Studies", icon: "Building2", pro: true },
  { to: "/translator", label: "Patois Translator", icon: "Languages" },
  { to: "/deal-builder", label: "Deal Builder", icon: "FileSignature", pro: true },
  { to: "/negotiation", label: "Negotiation Sim", icon: "Swords", pro: true },
  { to: "/deal-analyzer", label: "Deal Analyzer", icon: "FileSearch", pro: true },
  { to: "/iq-test", label: "Business IQ Test", icon: "BrainCircuit" },
  { to: "/challenges", label: "Challenges", icon: "Trophy" },
  { to: "/resources", label: "Resource Vault", icon: "BookMarked" },
];

const BOTTOM = [
  { to: "/dashboard", label: "Home", icon: "LayoutDashboard" },
  { to: "/mentor", label: "ECO", icon: "Sparkles" },
  { to: "/knowledge", label: "Learn", icon: "Library" },
  { to: "/university", label: "Paths", icon: "GraduationCap" },
];

function NavItem({ item, active, onClick }) {
  return (
    <Link
      to={item.to}
      onClick={onClick}
      data-testid={`nav-${item.to.replace("/", "")}`}
      className={`group flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
        active ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
      }`}
    >
      <Icon name={item.icon} size={18} className={active ? "text-[#4d8bff]" : ""} />
      <span className="font-medium">{item.label}</span>
      {item.pro && <Crown size={13} className="ml-auto text-amber-400" />}
    </Link>
  );
}

function Brand() {
  return (
    <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-logo">
      <div className="h-9 w-9 rounded-xl bg-[#0055FF] flex items-center justify-center shadow-[0_0_18px_rgba(0,85,255,0.5)]">
        <span className="font-display font-bold text-white text-lg">E</span>
      </div>
      <div className="leading-none">
        <div className="font-display font-bold text-white tracking-tight">ECO</div>
        <div className="font-mono-eco text-[9px] uppercase tracking-[0.2em] text-slate-500">Entrepreneur OS</div>
      </div>
    </Link>
  );
}

function GlobalSearch({ className = "" }) {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`); }}
      className={className}
    >
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search topics, terms, companies…"
          className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-white/25 transition-all"
        />
      </div>
    </form>
  );
}

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isActive = (to) => location.pathname === to || (to !== "/dashboard" && location.pathname.startsWith(to));
  const isPro = user?.is_pro || user?.tier === "pro";

  return (
    <div className="min-h-screen bg-[#050505] text-slate-200">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-72 flex-col glass border-r border-white/5 z-40">
        <div className="px-6 py-6"><Brand /></div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto no-scrollbar">
          {NAV.map((item) => <NavItem key={item.to} item={item} active={isActive(item.to)} />)}
        </nav>
        <div className="p-3 border-t border-white/5">
          {!isPro && (
            <Link to="/pricing" data-testid="sidebar-upgrade-btn" className="btn-accent w-full mb-2 text-sm">
              <Zap size={15} /> Upgrade to Pro
            </Link>
          )}
          <Link to="/account" data-testid="sidebar-account" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${isActive("/account") ? "bg-white/10" : "hover:bg-white/5"}`}>
            <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : <span className="text-sm font-semibold text-white">{(user?.name || "U")[0]}</span>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{isPro ? "Pro member" : "Free plan"}</div>
            </div>
            {isPro && <Crown size={14} className="text-amber-400" />}
          </Link>
        </div>
      </aside>

      {/* Main column */}
      <div className="md:pl-72">
        {/* Top bar */}
        <header className="sticky top-0 z-30 glass border-b border-white/5">
          <div className="flex items-center gap-3 px-4 md:px-8 py-3">
            <div className="md:hidden"><Brand /></div>
            <GlobalSearch className="hidden md:block flex-1 max-w-md" />
            <div className="flex-1 md:hidden" />
            <button data-testid="mobile-search-btn" onClick={() => navigate("/search")} className="md:hidden h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
              <Search size={17} className="text-slate-300" />
            </button>
            <div className="hidden md:flex items-center gap-3 ml-auto">
              {isPro ? (
                <span className="eco-tag flex items-center gap-1.5 text-amber-300 border-amber-400/20 bg-amber-400/10"><Crown size={13} /> PRO</span>
              ) : (
                <Link to="/pricing" className="btn-secondary text-sm py-2">Upgrade</Link>
              )}
            </div>
            {/* mobile menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="md:hidden h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                  <Menu size={18} className="text-slate-300" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0A0A0A] border-white/10 text-white w-[300px] p-0">
                <div className="px-5 py-5 border-b border-white/5"><Brand /></div>
                <nav className="px-3 py-3 space-y-1 overflow-y-auto max-h-[calc(100vh-200px)]">
                  {NAV.map((item) => <NavItem key={item.to} item={item} active={isActive(item.to)} onClick={() => setOpen(false)} />)}
                  <NavItem item={{ to: "/account", label: "Account", icon: "User" }} active={isActive("/account")} onClick={() => setOpen(false)} />
                </nav>
                <div className="p-3 border-t border-white/5 space-y-2">
                  {!isPro && <Link to="/pricing" onClick={() => setOpen(false)} className="btn-accent w-full text-sm"><Zap size={15} /> Upgrade to Pro</Link>}
                  <button data-testid="mobile-logout-btn" onClick={() => { setOpen(false); logout(); }} className="btn-secondary w-full text-sm"><LogOut size={15} /> Sign out</button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </header>

        <main className="px-4 md:px-8 py-6 pb-28 md:pb-12 max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500">
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-white/10 pb-safe">
        <div className="flex justify-around items-center px-2 pt-2 pb-2">
          {BOTTOM.map((item) => {
            const active = isActive(item.to);
            return (
              <Link key={item.to} to={item.to} data-testid={`bottomnav-${item.to.replace("/", "")}`} className="flex flex-col items-center gap-1 px-3 py-1 min-w-[60px]">
                <Icon name={item.icon} size={20} className={active ? "text-[#4d8bff]" : "text-slate-500"} />
                <span className={`text-[10px] font-medium ${active ? "text-white" : "text-slate-500"}`}>{item.label}</span>
              </Link>
            );
          })}
          <button onClick={() => setOpen(true)} data-testid="bottomnav-more" className="flex flex-col items-center gap-1 px-3 py-1 min-w-[60px]">
            <Menu size={20} className="text-slate-500" />
            <span className="text-[10px] font-medium text-slate-500">More</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
